/**
 * /pressure-testing/jobs/:id — one test job: its details, its tests, and the
 * writes on it (fieldiq-api#915 `GET /jobs/:id`, `PATCH /jobs/:id`,
 * `POST /jobs/:id/tests`, `PATCH /tests/:id`).
 *
 * THE STATUS MACHINE IS THE API'S. Start shows on a scheduled job, Complete on
 * an active one, Cancel on either; complete and cancelled are terminal and
 * show none. Complete is never pre-checked here: the API refuses a job with no
 * tests or a pending test with `code: 'tests_incomplete'` and the list in
 * `pending_tests`, and that list is drawn on the page as sent — the screen
 * does not count pending tests itself to decide whether to offer the button.
 *
 * The tallies in the band are the job's `test_counts` as returned. Every
 * pressure is the API's figure in the tenant's unit, whole and comma-separated.
 *
 * HOLD AND CHART (PT-3). The Hold column shows the server-stamped window a
 * technician recorded in the field — start, end, and how long it held against
 * the test's hold minutes, both ends the server's. The Chart column opens the
 * chart photo claimed onto the test, through the authenticated download door
 * (PtChartPhoto.tsx).
 *
 * CERTIFICATE (PT-4). The card under the tests opens the job's certificate in
 * its review page, or offers Generate on a complete job that has none
 * (PtJobCertificateCard.tsx).
 *
 * TICKETS (PT-5). The card under the certificate shows the job's ticket mode
 * (changeable until its first ticket) and its field tickets, each opening the
 * ticket's own page (PtJobTicketsCard.tsx).
 *
 * WHO: job writes are PT_DISPATCH_ROLES; tests are those roles on any job, and
 * a field worker on a job assigned to them (canWritePtTests). The API enforces
 * both; buttons it would refuse are left off.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { CalendarDays, CheckCircle2, ClipboardList, Clock, Loader2, Pencil, PlayCircle, Plus, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { displayValue } from '../../features/fieldiq-handoff/contracts';
import { usePtJob, useUpdatePtJob } from '../../hooks/usePressureTesting';
import { formatCalendarDate, formatInstant } from '../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { canDispatchPt, canWritePtTests, formatPressure, ptResultSkin, ptTicketModeLabel } from '../../lib/pressure-testing';
import type { PtJobDetail, PtPressureUnit, PtTest } from '../../types/pressure-testing';
import { CancelJobDialog, ptWriteError, TestDialog, type PtWriteError } from './PtJobDialogs';
import { PT_SECONDARY, PtDialog, PtEmpty, PtPanel, PtSpec } from './PtBits';
import { PtChartImage } from './PtChartPhoto';
import { formatHoldTime, holdClockAt } from '../../lib/pt-hold-clock';
import { PtJobStatusBadge } from './TestJobsScreen';
import { PtJobCertificateCard } from './PtJobCertificateCard';
import { PtJobTicketsCard } from './PtJobTicketsCard';

const day = (value: string | null | undefined) => formatCalendarDate(value) ?? '—';

function ResultBadge({ result }: { result: string | null | undefined }) {
  const skin = ptResultSkin(result);
  return (
    <span
      data-result={result ?? ''}
      className={`inline-flex w-fit items-center whitespace-nowrap rounded-fi-pill border border-black/20 px-2.5 py-1 text-[0.68rem] font-black uppercase tracking-[0.06em] leading-none text-fi-signal-ink ${skin.fill}`}
    >
      {skin.label}
    </span>
  );
}

/**
 * The hold as recorded: the two server stamps and how long they span. A hold
 * still running reads "running"; this register does not tick.
 */
function HoldCell({ test, tz }: { test: PtTest; tz: string | null }) {
  const length = test.hold_minutes == null ? '—' : `${test.hold_minutes} min`;
  if (!test.started_at) {
    return (
      <>
        <p>{length}</p>
        <p className="mt-0.5 text-xs text-fieldiq-muted">Not started</p>
      </>
    );
  }
  const time = (v: string | null) => formatInstant(v, tz, { hour: 'numeric', minute: '2-digit', hour12: true }) ?? '—';
  const held = test.ended_at ? formatHoldTime(holdClockAt(test.started_at, test.ended_at, test.hold_minutes, 0, 0).elapsedSeconds) : null;
  return (
    <>
      <p>{held ? `${held} of ${length}` : `running · ${length}`}</p>
      <p className="mt-0.5 text-xs text-fieldiq-muted" data-testid="pt-hold-window">
        {time(test.started_at)} – {test.ended_at ? time(test.ended_at) : '…'}
      </p>
    </>
  );
}

const instrumentText = (i: { name: string | null; serial_number: string | null } | null | undefined) =>
  i ? `${i.name || '—'}${i.serial_number ? ` · ${i.serial_number}` : ''}` : '—';

/** A secondary action on the navy band: white/25 edge, no colour class (it inherits the band's ink). */
const BAND_SECONDARY =
  'inline-flex h-10 items-center gap-1.5 rounded-fi-control border border-white/25 px-4 text-sm font-bold transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60';

export default function TestJobDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const query = usePtJob(id);
  const update = useUpdatePtJob();
  const [cancelling, setCancelling] = useState(false);
  const [editing, setEditing] = useState<PtTest | 'new' | null>(null);
  const [refusal, setRefusal] = useState<PtWriteError | null>(null);
  const [chartOf, setChartOf] = useState<PtTest | null>(null);

  const job: PtJobDetail | null = query.data?.data ?? null;
  const unit: PtPressureUnit | null = query.data?.meta.pressure_unit ?? null;
  const tz = query.data?.meta.time_zone ?? null;
  const failure = queryLoadFailure(query);
  const notFound = (queryFailureError(query) as { response?: { status?: number } } | null)?.response?.status === 404;

  const editable = job?.status === 'scheduled' || job?.status === 'active';
  const canDispatch = canDispatchPt(user?.role) && !!job;
  const canTests = editable && canWritePtTests(user, job) && !!unit;
  const tests = job?.tests ?? [];

  const transition = (to: 'active' | 'complete') => {
    if (!job) return;
    setRefusal(null);
    update.mutate(
      { id: job.id, body: { status: to } },
      {
        onSuccess: () => toast.success(to === 'active' ? `${job.job_number} started` : `${job.job_number} complete`),
        onError: (err) => setRefusal(ptWriteError(err, to === 'active' ? 'The job was not started.' : 'The job was not completed.')),
      },
    );
  };

  const actions = job ? (
    <div className="flex flex-wrap items-center gap-2">
      <PtJobStatusBadge status={job.status} />
      {canDispatch && job.status === 'scheduled' && (
        <button type="button" className="fi-action fi-action--standard fi-action--primary" disabled={update.isPending} onClick={() => transition('active')}>
          <PlayCircle className="size-4" aria-hidden="true" />
          Start
        </button>
      )}
      {canDispatch && job.status === 'active' && (
        <button type="button" className="fi-action fi-action--standard fi-action--primary" disabled={update.isPending} onClick={() => transition('complete')}>
          {update.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <CheckCircle2 className="size-4" aria-hidden="true" />}
          Complete
        </button>
      )}
      {canDispatch && editable && (
        <button type="button" className={BAND_SECONDARY} onClick={() => setCancelling(true)}>
          <XCircle className="size-4" aria-hidden="true" />
          Cancel job
        </button>
      )}
    </div>
  ) : undefined;

  const counts = job?.test_counts ?? null;

  // A refusal describes the job as it was. Once the job is read again (a test
  // saved, a status moved), the API's new answer replaces it.
  useEffect(() => setRefusal(null), [query.dataUpdatedAt]);

  return (
    <div data-testid="pt-job-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Pressure testing · Test job"
        title={job?.job_number || (query.isLoading ? 'Test job' : '—')}
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={actions}
        stats={[
          {
            icon: <ClipboardList className="size-4" aria-hidden="true" />,
            label: 'Tests',
            sub: 'Recorded',
            tone: 'neutral',
            loading: query.isLoading,
            value: displayValue(counts?.total),
          },
          {
            icon: <CheckCircle2 className="size-4" aria-hidden="true" />,
            label: 'Passed',
            tone: 'success',
            loading: query.isLoading,
            value: displayValue(counts?.pass),
          },
          {
            icon: <Clock className="size-4" aria-hidden="true" />,
            label: 'Pending',
            sub: counts && typeof counts.fail === 'number' ? `${counts.fail} failed` : undefined,
            tone: 'accent',
            loading: query.isLoading,
            value: displayValue(counts?.pending),
          },
          {
            icon: <CalendarDays className="size-4" aria-hidden="true" />,
            label: 'Date',
            tone: 'neutral',
            loading: query.isLoading,
            value: day(job?.scheduled_date),
          },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading test job" />
            </div>
          ) : notFound ? (
            <PtPanel eyebrow="Test job" title="Test job">
              <PtEmpty>
                This test job was not found. <Link to="/pressure-testing/jobs" className="font-bold underline">Back to test jobs</Link>
              </PtEmpty>
            </PtPanel>
          ) : failure ? (
            <PtPanel eyebrow="Test job" title="Test job">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="this test job" />
            </PtPanel>
          ) : !job ? (
            <PtPanel eyebrow="Test job" title="Test job">
              <PtEmpty>This test job was not found.</PtEmpty>
            </PtPanel>
          ) : (
            <>
              {refusal && (
                <div
                  role="alert"
                  data-testid="pt-job-refusal"
                  className="rounded-fi-panel border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text"
                >
                  <p className="font-bold">{refusal.message}</p>
                  {refusal.pendingTests && refusal.pendingTests.length > 0 && (
                    <ul className="mt-2 list-disc space-y-0.5 pl-5" data-testid="pt-pending-tests">
                      {refusal.pendingTests.map((t) => (
                        <li key={t.id}>
                          {t.test_type_name} #{t.number} — no result recorded
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {job.status === 'cancelled' && (
                <div className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-steel bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text" data-testid="pt-cancel-reason-shown">
                  <span className="font-bold">Cancelled.</span> {job.cancel_reason || '—'}
                </div>
              )}

              <PtPanel eyebrow="Test job" title="Details" testId="pt-job-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <PtSpec label="Customer" value={job.customer_name || '—'} />
                  <PtSpec label="Contact" value={job.customer_contact_name || '—'} />
                  <PtSpec label="PO" value={job.po_number || '—'} mono />
                  <PtSpec label="AFE" value={job.afe_number || '—'} mono />
                  <PtSpec label="Location" value={job.location_name || '—'} />
                  <PtSpec label="UWI" value={job.uwi || '—'} mono />
                  <PtSpec label="LSD" value={job.lsd || '—'} mono />
                  <PtSpec label="Province / state" value={job.province_state || '—'} />
                  <PtSpec label="Technician" value={job.technician_name || 'Not assigned'} />
                  <PtSpec label="Ticket mode" value={ptTicketModeLabel(job.ticket_mode)} />
                  <PtSpec label="Scheduled" value={day(job.scheduled_date)} />
                  <PtSpec label="Started" value={formatInstant(job.started_at, tz) ?? '—'} />
                  <PtSpec label="Completed" value={formatInstant(job.completed_at, tz) ?? '—'} />
                </dl>
                <div className="border-t border-fieldiq-border px-4 py-4">
                  <p className="text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Scope</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm text-fieldiq-text">{job.scope || '—'}</p>
                </div>
              </PtPanel>

              <PtPanel
                eyebrow="Pressure tests"
                title="Tests"
                testId="pt-job-tests"
                actions={
                  canTests ? (
                    <button type="button" className={PT_SECONDARY} onClick={() => setEditing('new')}>
                      <Plus className="size-4" aria-hidden="true" />
                      Add test
                    </button>
                  ) : undefined
                }
                footer={!editable ? `This job is ${job.status}; its tests can no longer be changed.` : undefined}
              >
                {tests.length === 0 ? (
                  <PtEmpty>No tests are recorded on this job.</PtEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1280px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr className="border-l-4 border-l-transparent">
                          <th scope="col" className="px-4 py-3">#</th>
                          <th scope="col" className="px-4 py-3">Test type</th>
                          <th scope="col" className="px-4 py-3 text-right">Test pressure</th>
                          <th scope="col" className="px-4 py-3 text-right">Hold</th>
                          <th scope="col" className="px-4 py-3 text-right">Start / end</th>
                          <th scope="col" className="px-4 py-3 text-right">Drop / allowable</th>
                          <th scope="col" className="px-4 py-3">Gauge / recorder</th>
                          <th scope="col" className="px-4 py-3">Chart</th>
                          <th scope="col" className="px-4 py-3">Result</th>
                          <th scope="col" className="w-[60px] px-4 py-3" />
                        </tr>
                      </thead>
                      <tbody>
                        {tests.map((t, n) => (
                          <tr key={t.id} data-testid="pt-test-row" className={`border-t border-l-4 border-fieldiq-border ${ptResultSkin(t.result).rail}`}>
                            <td className="px-4 py-3 font-mono text-sm text-fieldiq-muted">{n + 1}</td>
                            <td className="px-4 py-3 text-sm font-semibold text-white">{t.test_type_name || '—'}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">
                              {formatPressure(t.test_pressure, unit)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">
                              <HoldCell test={t} tz={tz} />
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">
                              {formatPressure(t.start_pressure, null)} / {formatPressure(t.end_pressure, unit)}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">
                              {formatPressure(t.pressure_drop, null)} / {formatPressure(t.allowable_drop, unit)}
                            </td>
                            <td className="px-4 py-3 text-xs text-fieldiq-text">
                              <p>{instrumentText(t.gauge)}</p>
                              <p className="mt-0.5 text-fieldiq-muted">{t.recorder ? instrumentText(t.recorder) : 'No recorder'}</p>
                            </td>
                            <td className="px-4 py-3 text-xs">
                              {t.chart ? (
                                <button
                                  type="button"
                                  className="font-bold text-fieldiq-text underline"
                                  onClick={() => setChartOf(t)}
                                  aria-label={`View chart photo, test ${n + 1}`}
                                  data-testid="pt-view-chart"
                                >
                                  View photo
                                </button>
                              ) : (
                                <span className="text-fieldiq-muted">{t.requires_recorder ? 'Not attached' : '—'}</span>
                              )}
                            </td>
                            <td className="px-4 py-3"><ResultBadge result={t.result} /></td>
                            <td className="px-4 py-3">
                              {canTests && (
                                <button
                                  type="button"
                                  className="inline-flex size-8 items-center justify-center rounded-fi-control text-fieldiq-muted transition hover:bg-fieldiq-dark/60 hover:text-fieldiq-text"
                                  aria-label={`Edit test ${n + 1}, ${t.test_type_name ?? 'test'}`}
                                  onClick={() => setEditing(t)}
                                >
                                  <Pencil className="size-4" aria-hidden="true" />
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </PtPanel>

              <PtJobCertificateCard job={job} canDispatch={canDispatch} timeZone={tz} />

              <PtJobTicketsCard job={job} canDispatch={canDispatch} timeZone={tz} today={query.data?.meta.today ?? null} />
            </>
          )}
        </div>
      </section>

      {chartOf && (
        <PtDialog
          open={!!chartOf}
          title={`Chart photo — ${chartOf.test_type_name ?? 'test'}`}
          onClose={() => setChartOf(null)}
          wide
          footer={
            <button type="button" className={PT_SECONDARY} onClick={() => setChartOf(null)}>
              Close
            </button>
          }
        >
          <PtChartImage test={chartOf} timeZone={tz} />
        </PtDialog>
      )}
      {job && cancelling && <CancelJobDialog open={cancelling} onClose={() => setCancelling(false)} job={job} />}
      {job && editing && (
        <TestDialog open={!!editing} onClose={() => setEditing(null)} job={job} test={editing === 'new' ? null : editing} unit={unit} />
      )}
    </div>
  );
}
